import { signOut } from 'firebase/auth';
import {
    Activity,
    Bell,
    Calendar,
    ChevronDown,
    ClipboardList,
    LayoutDashboard,
    LogOut,
    Shield,
    User,
    UserCheck,
    Users
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useComplaintsCount } from '../../hooks/useComplaintsCount';
import { auth } from "../../network/firebaseConfig";
import CountBadge from '../countdown/CountBase';
import { useMyActiveComplaints } from '../../hooks/useMyActiveComplaintsCount';
import NotificationPopup from './NotificationPopup';

const Navbar = ({ user }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false);
    const avatarUrl = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=150";

    const counts = useComplaintsCount();
    const { count, complaints } = useMyActiveComplaints();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/auth");
            toast.success("Logged out successfully!");
        } catch (error) {
            console.error("Logout failed:", error);
            toast.error("Error signing out. Please try again.");
        }
    };

    const isActive = (path) => location.pathname === path;

    return (
        <nav className="bg-white border-b border-gray-200 px-4 md:px-6 py-2 shadow-sm sticky top-0 z-50">
            <div className="max-w-7xl mx-auto flex items-center justify-between">

                {/* Left Side: Brand Logo */}
                <Link to="/" className="flex items-center space-x-3 cursor-pointer h-14">
                    <img
                        src="/assets/logo.png"
                        alt="Bijoy IT Complaint Tracker"
                        className="h-16 w-auto object-contain overflow-hidden"
                    />
                </Link>

                {/* Right Side: User Avatar & Info */}
                <div className="flex items-center space-x-3 md:space-x-4">
                    {/* Mobile Menu Toggle */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>

                    {/* Notification Bell */}
                    <button
                        onClick={() => setIsNotificationPopupOpen(true)}
                        className="relative p-1 rounded-full hover:bg-gray-100 transition-colors">
                        <Bell className="w-5 h-5 text-gray-600" />
                        {count > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-600 font-bold shadow-md text-white text-xs text-center">{count}</span>
                        )}
                    </button>

                    {isNotificationPopupOpen && (
                        <NotificationPopup
                            complaints={complaints}
                            isOpen={isNotificationPopupOpen}
                            setIsOpen={setIsNotificationPopupOpen}
                        />
                    )}
                    {/* User Text Details */}
                    <div className="hidden sm:block text-right">
                        <p className="text-sm font-semibold text-gray-800 leading-tight">
                            {user?.name || "Guest User"}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                            {user?.role || "No Role"}
                        </p>
                    </div>

                    {/* User Avatar with Dropdown Toggle */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-pink-500 rounded-full p-0.5 hover:ring-2 hover:ring-pink-200 transition-all"
                    >
                        <img
                            src={user?.avatarUrl || avatarUrl}
                            alt={user?.name || "User Avatar"}
                            className="w-10 h-10 rounded-full border-2 border-gray-200 object-cover"
                        />
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu (Desktop) */}
                    {isMenuOpen && (
                        <>
                            {/* Backdrop */}
                            <div
                                className="fixed inset-0 z-40"
                                onClick={() => setIsMenuOpen(false)}
                            />

                            <div className="absolute right-0 top-14 w-72 bg-white border border-gray-200 rounded-xl shadow-2xl py-2 z-50 animate-fade-in-down">
                                {/* User Info Header */}
                                <div className="px-4 py-3 border-b border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={user?.avatarUrl || avatarUrl}
                                            alt={user?.name || "User Avatar"}
                                            className="w-12 h-12 rounded-full border-2 border-gray-200 object-cover"
                                        />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800">{user?.name || "Guest User"}</p>
                                            <p className="text-xs text-gray-500 capitalize">{user?.role || "No Role"}</p>
                                            <p className="text-xs text-gray-400">{user?.email || "No email"}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Attendance & My Attendance */}
                                <div className="px-4 py-3 border-b border-gray-100">
                                    <Link
                                        to="/attendance"
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        Attendance
                                    </Link>
                                    <Link
                                        to="/my-attendance"
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <UserCheck className="w-4 h-4 text-gray-400" />
                                        My Attendance
                                    </Link>
                                </div>

                                {/* Menu Items */}
                                <div className="py-1">
                                    <Link
                                        to="/"
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <LayoutDashboard className="w-4 h-4 text-gray-400" />
                                        Dashboard
                                    </Link>

                                    <Link
                                        to="/complaints"
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <ClipboardList className="w-4 h-4 text-gray-400" />
                                        Complaint History
                                        <CountBadge count={counts.total} type="info" className="ml-auto" />
                                    </Link>

                                    <Link
                                        to="/active-complaints"
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <Activity className="w-4 h-4 text-gray-400" />
                                        Active Complaints
                                        <CountBadge count={counts.inOperation} type="success" className="ml-auto" />
                                    </Link>

                                    <Link
                                        to="/my-complaints"
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <Users className="w-4 h-4 text-gray-400" />
                                        My Complaints
                                    </Link>

                                    <Link
                                        to="/profile"
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <User className="w-4 h-4 text-gray-400" />
                                        Profile Settings
                                    </Link>

                                    {user?.role === 'admin' && (
                                        <>
                                            <div className="border-t border-gray-100 my-1"></div>
                                            <Link
                                                to="/admin"
                                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <Shield className="w-4 h-4 text-purple-400" />
                                                Admin Dashboard
                                            </Link>
                                        </>
                                    )}

                                    <div className="border-t border-gray-100 my-1"></div>

                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors w-full"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Sign Out
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Mobile Navigation Links - NOW ALIGNED WITH DESKTOP */}
            {isMenuOpen && (
                <div className="lg:hidden mt-3 pt-3 border-t border-gray-200 space-y-1">
                    <Link
                        to="/"
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/')
                            ? 'bg-pink-50 text-pink-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                    </Link>

                    <Link
                        to="/attendance"
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/attendance')
                            ? 'bg-pink-50 text-pink-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <Calendar className="w-4 h-4" />
                        Attendance
                    </Link>

                    <Link
                        to="/my-attendance"
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/my-attendance')
                            ? 'bg-pink-50 text-pink-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <UserCheck className="w-4 h-4" />
                        My Attendance
                    </Link>

                    <Link
                        to="/complaints"
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/complaints')
                            ? 'bg-pink-50 text-pink-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <ClipboardList className="w-4 h-4" />
                        Complaint History
                        <CountBadge count={counts.total} type="info" className="ml-auto" />
                    </Link>

                    <Link
                        to="/active-complaints"
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/active-complaints')
                            ? 'bg-pink-50 text-pink-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <Activity className="w-4 h-4" />
                        Active Complaints
                        <CountBadge count={counts.inOperation} type="success" className="ml-auto" />
                    </Link>

                    <Link
                        to="/my-complaints"
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/my-complaints')
                            ? 'bg-pink-50 text-pink-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <Users className="w-4 h-4" />
                        My Complaints
                    </Link>

                    <Link
                        to="/profile"
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/profile')
                            ? 'bg-pink-50 text-pink-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        onClick={() => setIsMenuOpen(false)}
                    >
                        <User className="w-4 h-4" />
                        Profile Settings
                    </Link>

                    {user?.role === 'admin' && (
                        <Link
                            to="/admin"
                            className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isActive('/admin')
                                ? 'bg-purple-50 text-purple-600'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <Shield className="w-4 h-4" />
                            Admin Dashboard
                        </Link>
                    )}

                    <div className="border-t border-gray-200 my-1"></div>

                    <button
                        onClick={handleLogout}
                        className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                    </button>
                </div>
            )}
        </nav>
    );
};

export default Navbar;